import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from "@tanstack/react-router";
import { Chooser } from "./routes/Chooser";
import { Onboarding } from "./routes/Onboarding";
import { CreateStore } from "./routes/CreateStore";
import { Home, ComingSoon } from "./routes/Home";
import { PartnerDashboard } from "./routes/Partner";
import { Accept } from "./routes/Accept";
import { StoreLayout } from "./components/Shell";

// Feature pages load on demand (specs 07 §9: route-level code splitting).
const GeneralSettings = lazyRouteComponent(() => import("./routes/settings/Settings"), "GeneralSettings");
const PlanSettings = lazyRouteComponent(() => import("./routes/settings/Billing"), "PlanSettings");
const BillingSettings = lazyRouteComponent(() => import("./routes/settings/Billing"), "BillingSettings");
const SettingsIndex = lazyRouteComponent(() => import("./routes/settings/Settings"), "SettingsIndex");
const SettingsLayout = lazyRouteComponent(() => import("./routes/settings/Settings"), "SettingsLayout");
const MetaobjectsIndex = lazyRouteComponent(() => import("./routes/content/Metaobjects"), "MetaobjectsIndex");
const StoreStatus = lazyRouteComponent(() => import("./routes/settings/Settings"), "StoreStatus");
const LanguagesSettings = lazyRouteComponent(() => import("./routes/settings/Languages"), "LanguagesSettings");
const TranslatePage = lazyRouteComponent(() => import("./routes/online-store/Translate"), "TranslatePage");
const UsersSettings = lazyRouteComponent(() => import("./routes/settings/Users"), "UsersSettings");
const AppsSettings = lazyRouteComponent(() => import("./routes/settings/Apps"), "AppsSettings");
const AppDetail = lazyRouteComponent(() => import("./routes/settings/Apps"), "AppDetail");
const AppInstall = lazyRouteComponent(() => import("./routes/settings/Apps"), "AppInstall");
const InstallChooser = lazyRouteComponent(() => import("./routes/settings/Apps"), "InstallChooser");
const NotificationSettings = lazyRouteComponent(() => import("./routes/settings/Notifications"), "NotificationSettings");
const FilesSettings = lazyRouteComponent(() => import("./routes/settings/Files"), "FilesSettings");
const ActivitySettings = lazyRouteComponent(() => import("./routes/settings/Activity"), "ActivitySettings");
const CustomDataSettings = lazyRouteComponent(() => import("./routes/settings/CustomData"), "CustomDataSettings");
const MetaobjectEntries = lazyRouteComponent(() => import("./routes/settings/CustomData"), "MetaobjectEntries");
const ProductList = lazyRouteComponent(() => import("./routes/products/ProductList"), "ProductList");
const ProductEditor = lazyRouteComponent(() => import("./routes/products/ProductEditor"), "ProductEditor");
const CollectionEditor = lazyRouteComponent(() => import("./routes/products/Collections"), "CollectionEditor");
const CollectionList = lazyRouteComponent(() => import("./routes/products/Collections"), "CollectionList");
const InventoryPage = lazyRouteComponent(() => import("./routes/inventory/InventoryPage"), "InventoryPage");
const TransferDetail = lazyRouteComponent(() => import("./routes/inventory/Transfers"), "TransferDetail");
const TransferList = lazyRouteComponent(() => import("./routes/inventory/Transfers"), "TransferList");
const PurchaseOrderDetail = lazyRouteComponent(() => import("./routes/inventory/PurchaseOrders"), "PurchaseOrderDetail");
const PurchaseOrderList = lazyRouteComponent(() => import("./routes/inventory/PurchaseOrders"), "PurchaseOrderList");
const CountDetail = lazyRouteComponent(() => import("./routes/inventory/Counts"), "CountDetail");
const CountList = lazyRouteComponent(() => import("./routes/inventory/Counts"), "CountList");
const InventorySettings = lazyRouteComponent(() => import("./routes/settings/Locations"), "InventorySettings");
const LocationsSettings = lazyRouteComponent(() => import("./routes/settings/Locations"), "LocationsSettings");
const LocationDetail = lazyRouteComponent(() => import("./routes/settings/Locations"), "LocationDetail");
const DiscountList = lazyRouteComponent(() => import("./routes/discounts/DiscountList"), "DiscountList");
const DiscountEditor = lazyRouteComponent(() => import("./routes/discounts/DiscountEditor"), "DiscountEditor");
const TaxSettings = lazyRouteComponent(() => import("./routes/settings/Taxes"), "TaxSettings");
const InvoiceSettings = lazyRouteComponent(() => import("./routes/settings/Invoices"), "InvoiceSettings");
const CustomerAccountsSettings = lazyRouteComponent(() => import("./routes/settings/CustomerAccounts"), "CustomerAccountsSettings");
const CustomerList = lazyRouteComponent(() => import("./routes/customers/CustomerList"), "CustomerList");
const CustomerDetail = lazyRouteComponent(() => import("./routes/customers/CustomerDetail"), "CustomerDetail");
const SegmentList = lazyRouteComponent(() => import("./routes/customers/Segments"), "SegmentList");
const SegmentEditor = lazyRouteComponent(() => import("./routes/customers/Segments"), "SegmentEditor");
const PrivacyRequests = lazyRouteComponent(() => import("./routes/customers/Privacy"), "PrivacyRequests");

const VariantEditor = lazyRouteComponent(() => import("./routes/products/VariantEditor"), "VariantEditor");
const ShippingSettings = lazyRouteComponent(() => import("./routes/settings/Shipping"), "ShippingSettings");
const ShippingProfileEditor = lazyRouteComponent(() => import("./routes/settings/ShippingProfile"), "ShippingProfileEditor");
const LocalDeliveryEditor = lazyRouteComponent(() => import("./routes/settings/LocalDelivery"), "LocalDeliveryEditor");
const CheckoutSettingsPage = lazyRouteComponent(() => import("./routes/settings/CheckoutSettings"), "CheckoutSettingsPage");
const ThemesPage = lazyRouteComponent(() => import("./routes/online-store/Themes"), "ThemesPage");
const CodeEditorPage = lazyRouteComponent(() => import("./routes/online-store/code/CodeEditor"), "CodeEditorPage");
const CustomizePage = lazyRouteComponent(() => import("./routes/online-store/customize/Customize"), "CustomizePage");
const OrderList = lazyRouteComponent(() => import("./routes/orders/OrderList"), "OrderList");
const OrderDetail = lazyRouteComponent(() => import("./routes/orders/OrderDetail"), "OrderDetail");
const FulfillOrder = lazyRouteComponent(() => import("./routes/orders/Fulfill"), "FulfillOrder");
const RefundOrder = lazyRouteComponent(() => import("./routes/orders/Refund"), "RefundOrder");
const DraftOrderList = lazyRouteComponent(() => import("./routes/orders/DraftOrders"), "DraftOrderList");
const DraftOrderEditor = lazyRouteComponent(() => import("./routes/orders/DraftOrders"), "DraftOrderEditor");
const AbandonedCheckoutList = lazyRouteComponent(() => import("./routes/orders/AbandonedCheckouts"), "AbandonedCheckoutList");
const AbandonedCheckoutDetail = lazyRouteComponent(() => import("./routes/orders/AbandonedCheckouts"), "AbandonedCheckoutDetail");
const PaymentsSettings = lazyRouteComponent(() => import("./routes/settings/Payments"), "PaymentsSettings");
const SearchDiscoveryPage = lazyRouteComponent(() => import("./routes/online-store/SearchDiscovery"), "SearchDiscoveryPage");
const FeaturesPage = lazyRouteComponent(() => import("./routes/online-store/Features"), "FeaturesPage");
const AnalyticsDashboard = lazyRouteComponent(() => import("./routes/analytics/Analytics"), "AnalyticsDashboard");
const ReportsLibrary = lazyRouteComponent(() => import("./routes/analytics/Analytics"), "ReportsLibrary");
const ReportView = lazyRouteComponent(() => import("./routes/analytics/Analytics"), "ReportView");
const LiveView = lazyRouteComponent(() => import("./routes/analytics/Analytics"), "LiveView");
const MarketingOverview = lazyRouteComponent(() => import("./routes/marketing/Marketing"), "MarketingOverview");
const CampaignList = lazyRouteComponent(() => import("./routes/marketing/Campaigns"), "CampaignList");
const CampaignEditor = lazyRouteComponent(() => import("./routes/marketing/Campaigns"), "CampaignEditor");
const AutomationList = lazyRouteComponent(() => import("./routes/marketing/Automations"), "AutomationList");
const AutomationEditor = lazyRouteComponent(() => import("./routes/marketing/Automations"), "AutomationEditor");
const FeedsPage = lazyRouteComponent(() => import("./routes/marketing/Feeds"), "FeedsPage");
const GiftCardList = lazyRouteComponent(() => import("./routes/products/GiftCards"), "GiftCardList");
const GiftCardDetail = lazyRouteComponent(() => import("./routes/products/GiftCards"), "GiftCardDetail");
const SEOPage = lazyRouteComponent(() => import("./routes/online-store/SEO"), "SEOPage");
const CustomerEventsSettings = lazyRouteComponent(() => import("./routes/settings/CustomerEvents"), "CustomerEventsSettings");
const ReviewsPage = lazyRouteComponent(() => import("./routes/engage/Reviews"), "ReviewsPage");
const ReturnsPage = lazyRouteComponent(() => import("./routes/engage/Returns"), "ReturnsPage");
const WishlistInsightsPage = lazyRouteComponent(() => import("./routes/engage/Insights"), "WishlistInsightsPage");
const StockInsightsPage = lazyRouteComponent(() => import("./routes/engage/Insights"), "StockInsightsPage");
const DomainsSettings = lazyRouteComponent(() => import("./routes/settings/Domains"), "DomainsSettings");
const PoliciesSettings = lazyRouteComponent(() => import("./routes/settings/Policies"), "PoliciesSettings");
const PageList = lazyRouteComponent(() => import("./routes/online-store/Pages"), "PageList");
const PageEditor = lazyRouteComponent(() => import("./routes/online-store/Pages"), "PageEditor");
const PreferencesPage = lazyRouteComponent(() => import("./routes/online-store/Preferences"), "PreferencesPage");
const ArticleList = lazyRouteComponent(() => import("./routes/content/BlogPosts"), "ArticleList");
const ArticleEditor = lazyRouteComponent(() => import("./routes/content/BlogPosts"), "ArticleEditor");
const CommentList = lazyRouteComponent(() => import("./routes/content/BlogPosts"), "CommentList");
const MenuList = lazyRouteComponent(() => import("./routes/content/Menus"), "MenuList");
const MenuEditor = lazyRouteComponent(() => import("./routes/content/Menus"), "MenuEditor");
const RedirectList = lazyRouteComponent(() => import("./routes/content/Redirects"), "RedirectList");
const FormSubmissions = lazyRouteComponent(() => import("./routes/content/FormSubmissions"), "FormSubmissions");
const root = createRootRoute({ component: Outlet });
const r = (path: string, component: Parameters<typeof createRoute>[0]["component"], parent: typeof root | typeof store | typeof settings = root) =>
  createRoute({ getParentRoute: () => parent, path, component });

const index = createRoute({ getParentRoute: () => root, path: "/", beforeLoad: () => { throw redirect({ to: "/stores" }); } });
const store = createRoute({ getParentRoute: () => root, path: "/store/$handle", component: StoreLayout });
const settings = createRoute({ getParentRoute: () => store, path: "/settings", component: SettingsLayout });

// Pages moved in the redesign (redesign.md §0.4): old URLs (bookmarks,
// emails, notifications) redirect to the new ones, keeping params and query.
const moved = (parent: typeof store | typeof settings, from: string, to: string) =>
  createRoute({ getParentRoute: () => parent, path: from, beforeLoad: ({ params, search }) => {
    throw redirect({ to: `/store/$handle${to}` as never, params: params as never, search: search as never, replace: true });
  } });
const movedInStore = [
  moved(store, "/content/metaobjects/$type", "/storefront/custom-content/$type"),
  moved(store, "/content/metaobjects", "/storefront/custom-content"),
  moved(store, "/content/files", "/storefront/files"),
  moved(store, "/content/menus/redirects", "/storefront/menus/redirects"),
  moved(store, "/content/menus/$menuId", "/storefront/menus/$menuId"),
  moved(store, "/content/menus", "/storefront/menus"),
  moved(store, "/content/blog-posts/comments", "/storefront/blog/comments"),
  moved(store, "/content/blog-posts/$articleId", "/storefront/blog/$articleId"),
  moved(store, "/content/blog-posts", "/storefront/blog"),
  moved(store, "/content/forms", "/messages"),
  moved(store, "/online-store/themes", "/storefront/themes"),
  moved(store, "/online-store/pages/$pageId", "/storefront/pages/$pageId"),
  moved(store, "/online-store/pages", "/storefront/pages"),
  moved(store, "/online-store/preferences", "/storefront/preferences"),
  moved(store, "/online-store/search", "/storefront/search"),
  moved(store, "/draft_orders/$draftId", "/orders/manual/$draftId"),
  moved(store, "/draft_orders", "/orders/manual"),
  moved(store, "/orders/abandoned/$checkoutId", "/orders/recovered/$checkoutId"),
  moved(store, "/orders/abandoned", "/orders/recovered"),
];
const movedInSettings = [
  moved(settings, "/general", "/settings/store-profile"),
  moved(settings, "/plan", "/settings/subscription"),
  moved(settings, "/users", "/settings/team"),
  moved(settings, "/payments", "/settings/payment-methods"),
  moved(settings, "/locations/$locationId", "/settings/warehouses/$locationId"),
  moved(settings, "/locations", "/settings/warehouses"),
  moved(settings, "/custom-data", "/settings/custom-fields"),
];

type InstallSearch = { client_id?: string; scope?: string; redirect_uri?: string; state?: string };
const installSearch = (s: Record<string, unknown>): InstallSearch =>
  Object.fromEntries((["client_id", "scope", "redirect_uri", "state"] as const).filter((k) => typeof s[k] === "string").map((k) => [k, s[k] as string]));
const tokenSearch = (s: Record<string, unknown>): { token?: string } => (typeof s.token === "string" ? { token: s.token } : {});
const accept = (path: string, kind: Parameters<typeof Accept>[0]["kind"]) =>
  createRoute({ getParentRoute: () => root, path, validateSearch: tokenSearch, component: () => <Accept kind={kind} /> });

const routeTree = root.addChildren([
  index,
  r("/stores", Chooser),
  r("/editor/$handle/themes/$themeId/code", CodeEditorPage),
  r("/editor/$handle/themes/$themeId/customize", CustomizePage),
  createRoute({ getParentRoute: () => root, path: "/stores/new", component: CreateStore,
    validateSearch: (s: Record<string, unknown>): { org?: string } => (typeof s.org === "string" ? { org: s.org } : {}) }),
  r("/onboarding", Onboarding),
  r("/partners/$org", PartnerDashboard),
  // A connected app's install link: choose the store, then consent there.
  createRoute({ getParentRoute: () => root, path: "/apps/install", component: InstallChooser, validateSearch: installSearch }),
  accept("/invitations/accept", "invitation"),
  accept("/ownership-transfers/accept", "ownership"),
  accept("/transfers/accept", "transfer"),
  accept("/organizations/accept", "organization"),
  store.addChildren([
    r("/", Home, store),
    r("/products", ProductList, store),
    r("/products/$productId", ProductEditor, store),
    r("/products/$productId/variants/$variantId", VariantEditor, store),
    r("/collections", CollectionList, store),
    r("/collections/$collectionId", CollectionEditor, store),
    createRoute({ getParentRoute: () => store, path: "/content", beforeLoad: ({ params }) => { throw redirect({ to: "/store/$handle/storefront/custom-content", params }); } }),
    r("/storefront/custom-content", MetaobjectsIndex, store),
    r("/storefront/custom-content/$type", MetaobjectEntries, store),
    r("/storefront/files", FilesSettings, store),
    r("/storefront/menus", MenuList, store),
    r("/storefront/menus/redirects", RedirectList, store),
    r("/storefront/menus/$menuId", MenuEditor, store),
    r("/storefront/blog", ArticleList, store),
    r("/storefront/blog/comments", CommentList, store),
    r("/storefront/blog/$articleId", ArticleEditor, store),
    r("/messages", FormSubmissions, store),
    createRoute({ getParentRoute: () => store, path: "/settings/files", beforeLoad: ({ params }) => { throw redirect({ to: "/store/$handle/storefront/files", params }); } }),
    createRoute({ getParentRoute: () => store, path: "/customers/privacy", beforeLoad: ({ params }) => { throw redirect({ to: "/store/$handle/settings/customer-privacy", params }); } }),
    settings.addChildren([
      ...movedInSettings,
      r("/", SettingsIndex, settings),
      r("/store-profile", GeneralSettings, settings),
      r("/subscription", PlanSettings, settings),
      r("/billing", BillingSettings, settings),
      r("/team", UsersSettings, settings),
      r("/payment-methods", PaymentsSettings, settings),
      r("/checkout", CheckoutSettingsPage, settings),
      r("/customer-accounts", CustomerAccountsSettings, settings),
      r("/shipping", ShippingSettings, settings),
      r("/shipping/profiles/$profileId", ShippingProfileEditor, settings),
      r("/shipping/local-delivery/$locationId", LocalDeliveryEditor, settings),
      r("/taxes", TaxSettings, settings),
      r("/invoices", InvoiceSettings, settings),
      r("/warehouses", LocationsSettings, settings),
      r("/warehouses/$locationId", LocationDetail, settings),
      r("/inventory", InventorySettings, settings),
      r("/domains", DomainsSettings, settings),
      r("/apps", AppsSettings, settings),
      createRoute({ getParentRoute: () => settings, path: "/apps/install", component: AppInstall, validateSearch: installSearch }),
      createRoute({ getParentRoute: () => settings, path: "/apps/$appId", component: AppDetail,
        validateSearch: (s: Record<string, unknown>): { tab?: "overview" | "configuration" | "credentials" | "webhooks" } =>
          (typeof s.tab === "string" && ["overview", "configuration", "credentials", "webhooks"].includes(s.tab) ? { tab: s.tab as "overview" } : {}) }),
      r("/policies", PoliciesSettings, settings),
      r("/notifications", NotificationSettings, settings),
      r("/custom-fields", CustomDataSettings, settings),
      r("/customer-privacy", PrivacyRequests, settings),
      r("/customer-events", CustomerEventsSettings, settings),
      r("/activity", ActivitySettings, settings),
      r("/store-status", StoreStatus, settings),
      r("/languages", LanguagesSettings, settings),
    ]),
    createRoute({ getParentRoute: () => store, path: "/inventory", component: InventoryPage,
      validateSearch: (s: Record<string, unknown>): { location?: string } => (typeof s.location === "string" ? { location: s.location } : {}) }),
    r("/inventory/counts", CountList, store),
    r("/inventory/counts/$countId", CountDetail, store),
    r("/transfers", TransferList, store),
    r("/transfers/$transferId", TransferDetail, store),
    r("/purchase-orders", PurchaseOrderList, store),
    r("/purchase-orders/$poId", PurchaseOrderDetail, store),
    r("/discounts", DiscountList, store),
    createRoute({ getParentRoute: () => store, path: "/discounts/$discountId", component: DiscountEditor,
      validateSearch: (s: Record<string, unknown>): { type?: "product" | "order" | "bxgy" | "shipping" } =>
        (typeof s.type === "string" && ["product", "order", "bxgy", "shipping"].includes(s.type) ? { type: s.type as "product" } : {}) }),
    createRoute({ getParentRoute: () => store, path: "/customers", component: CustomerList,
      validateSearch: (s: Record<string, unknown>): { segment?: string } => (typeof s.segment === "string" ? { segment: s.segment } : {}) }),
    r("/storefront/themes", ThemesPage, store),
    // The theme editors open in their own tab, outside the admin shell.
    createRoute({ getParentRoute: () => store, path: "/storefront/themes/$themeId/code", beforeLoad: ({ params }) => {
      throw redirect({ to: "/editor/$handle/themes/$themeId/code", params: params as never, replace: true });
    } }),
    r("/storefront/pages", PageList, store),
    r("/storefront/pages/$pageId", PageEditor, store),
    r("/storefront/preferences", PreferencesPage, store),
    r("/storefront/search", SearchDiscoveryPage, store),
    r("/storefront/features", FeaturesPage, store),
    r("/storefront/features/wishlist", WishlistInsightsPage, store),
    r("/storefront/features/back_in_stock", StockInsightsPage, store),
    r("/reviews", ReviewsPage, store),
    r("/gift_cards", GiftCardList, store),
    r("/gift_cards/$giftCardId", GiftCardDetail, store),
    r("/marketing", MarketingOverview, store),
    r("/marketing/campaigns", CampaignList, store),
    r("/marketing/campaigns/new", CampaignEditor, store),
    r("/marketing/campaigns/$campaignId", CampaignEditor, store),
    r("/marketing/automations", AutomationList, store),
    r("/marketing/automations/$flowId", AutomationEditor, store),
    r("/marketing/feeds", FeedsPage, store),
    r("/storefront/seo", SEOPage, store),
    r("/analytics", AnalyticsDashboard, store),
    r("/analytics/reports", ReportsLibrary, store),
    r("/analytics/reports/$reportKey", ReportView, store),
    r("/analytics/live", LiveView, store),
    r("/orders/returns", ReturnsPage, store),
    r("/storefront/translate", TranslatePage, store),
    createRoute({ getParentRoute: () => store, path: "/orders", component: OrderList,
      validateSearch: (s: Record<string, unknown>): { tab?: string } => (typeof s.tab === "string" ? { tab: s.tab } : {}) }),
    r("/orders/$orderId", OrderDetail, store),
    createRoute({ getParentRoute: () => store, path: "/orders/$orderId/fulfill", component: FulfillOrder,
      validateSearch: (s: Record<string, unknown>): { fo?: string } => (typeof s.fo === "string" ? { fo: s.fo } : {}) }),
    r("/orders/$orderId/refund", RefundOrder, store),
    r("/orders/manual", DraftOrderList, store),
    r("/orders/manual/$draftId", DraftOrderEditor, store),
    r("/orders/recovered", AbandonedCheckoutList, store),
    r("/orders/recovered/$checkoutId", AbandonedCheckoutDetail, store),
    r("/customers/segments", SegmentList, store),
    r("/customers/segments/$segmentId", SegmentEditor, store),
    r("/customers/$customerId", CustomerDetail, store),
    ...movedInStore,
    r("$", ComingSoon, store), // modules not built yet
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
